import React, { useMemo } from 'react'
import { getIconComponent } from '@/components/performance/icon-utils'
import { Badge } from '@/components/ui/Badge'
import type { EvaluationCategory, EvaluationItem, EvaluationScore } from '@/types/performance'

interface ThemeGridProps {
  themes: EvaluationCategory[]
  allScores: EvaluationScore[]
  allItems: EvaluationItem[]
  onThemeClick?: (themeId: string) => void
}

function computeThemeStats(scores: EvaluationScore[], items: EvaluationItem[], categoryId: string) {
  const categoryItems = items.filter((i) => i.categoryId === categoryId)
  let total = 0
  let count = 0
  let scoredCount = 0
  for (const item of categoryItems) {
    const score = scores.find((s) => s.itemId === item.itemId)
    if (score && score.score !== null && !score.skipped) {
      total += score.score
      count++
      scoredCount++
    }
  }
  const avg = count > 0 ? total / count : 0
  const isComplete = categoryItems.length > 0 && scoredCount === categoryItems.length
  return { avg, itemCount: categoryItems.length, isComplete }
}

function scoreColor(avg: number): string {
  if (avg === 0) return 'text-brand-gray'
  if (avg >= 4) return 'text-brand-primary'
  if (avg >= 3) return 'text-green-400'
  if (avg >= 2) return 'text-warning'
  return 'text-error'
}

function progressPercent(avg: number): number {
  return (avg / 5) * 100
}

export const ThemeGrid: React.FC<ThemeGridProps> = ({ themes, allScores, allItems, onThemeClick }) => {
  const sorted = useMemo(
    () => [...themes].sort((a, b) => a.order - b.order),
    [themes]
  )

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="theme-grid">
      {sorted.map((theme) => {
        const { avg, itemCount, isComplete } = computeThemeStats(allScores, allItems, theme.categoryId)
        const Icon = getIconComponent(theme.icon)

        return (
          <button
            key={theme.categoryId}
            type="button"
            onClick={() => onThemeClick?.(theme.categoryId)}
            className="card-interactive p-3 rounded-lg flex flex-col items-center gap-2 text-center hover:border-brand-primary/30 transition-all"
            data-testid={`theme-card-${theme.categoryId}`}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${theme.colorScheme?.bgClass ?? 'bg-brand-dark-lighter'}`}>
              <Icon className={`w-5 h-5 ${theme.colorScheme?.textClass ?? 'text-brand-gray'}`} />
            </div>
            <span className="text-xs text-white font-medium line-clamp-2">{theme.name}</span>
            <span className="text-[10px] text-brand-gray">{itemCount} {itemCount === 1 ? 'item' : 'itens'}</span>
            <span className={`text-lg font-bold ${scoreColor(avg)}`}>
              {avg > 0 ? avg.toFixed(1) : '—'}
            </span>
            <div className="w-full h-1 bg-brand-gray/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-primary rounded-full transition-all"
                style={{ width: `${progressPercent(avg)}%` }}
              />
            </div>
            <Badge variant={isComplete ? 'success' : 'warning'}>
              {isComplete ? 'Completo' : 'Incompleto'}
            </Badge>
          </button>
        )
      })}
    </div>
  )
}
